/**
 * 筛选器配置工具函数
 * 用于数据转换、校验等通用逻辑
 */

import { AI_ANALYSIS_TYPE } from '@dbl/core/dashboard';
import type {
  IFilterState,
  IFilterConfig,
  IDateDefaultValue,
  IStringDefaultValue,
  INumberDefaultValue,
  IValidation,
  IViewListItem,
} from './types';

type TResolvedFilterType = 'date' | 'string' | 'number' | '';

/**
 * 解析“实际保存/运行”的筛选器类型。
 *
 * 说明：
 * - UI 的 Filter Type 只有 date / value(string) 两种；
 * - 当选择的是数值字段（number/float/percent）时，UI 仍显示 value，
 *   但保存的 filterType 必须为 number（与旧版 Filter.tsx 行为一致）。
 */
export const resolveFilterType = (state: IFilterState): TResolvedFilterType => {
  if (
    state.filterType === 'string' &&
    state.columnInfo?.isNumeric === true
  ) {
    return 'number';
  }
  return (state.filterType || '') as TResolvedFilterType;
};

/**
 * 校验筛选器基础字段
 * @param state - 筛选器状态
 * @returns 校验错误信息，如果通过则返回 null
 */
export const validateFilterBasicFields = (
  state: IFilterState
): { field: string; message: string; } | null => {
  if (!state.dataset) {
    return { field: 'dataset', message: 'dbl_common_cannot_be_empty' };
  }
  if (!state.column) {
    return { field: 'column', message: 'dbl_common_cannot_be_empty' };
  }
  if (!state.filterType) {
    return { field: 'filterType', message: 'dbl_common_cannot_be_empty' };
  }
  if (!state.filterName?.trim()) {
    return { field: 'filterName', message: 'dbl_common_cannot_be_empty' };
  }
  return null;
};

/**
 * 校验默认值配置
 * @param state - 筛选器状态
 * @returns 校验错误信息，如果通过则返回 null
 */
export const validateDefaultValue = (
  state: IFilterState
): { field: string; message: string; } | null => {
  const filterType = resolveFilterType(state);
  const { defaultValue } = state;

  if (filterType === 'date') {
    const dateValue = defaultValue as IDateDefaultValue | undefined;
    if (!dateValue?.mode) {
      // 日期类型：未选择 Default Type
      return { field: 'defaultType', message: 'dbl_common_cannot_be_empty' };
    }
    if (dateValue.mode === 'dynamic' && !dateValue.dynamicValue) {
      return { field: 'defaultValue', message: 'dbl_common_cannot_be_empty' };
    }
    if (dateValue.mode === 'static' && !dateValue.staticValue?.start) {
      return { field: 'defaultValue', message: 'dbl_common_cannot_be_empty' };
    }
  }

  if (filterType === 'number') {
    const numberValue = defaultValue as INumberDefaultValue | undefined;
    // 数值类型：区间操作时需要两个值，其他操作需要一个值

    if (numberValue?.operation === 'between') {
      if (!numberValue.value || numberValue.value.length < 2) {
        return { field: 'defaultValue', message: 'dbl_common_cannot_be_empty' };
      }
    } else if (
      numberValue?.operation !== 'null' &&
      numberValue?.operation !== 'notNull'
    ) {
      if (!numberValue?.value || numberValue.value.length === 0) {
        return { field: 'defaultValue', message: 'dbl_common_cannot_be_empty' };
      }
    }
  }

  return null;
};

/**
 * 收集所有字段级校验错误
 * 用于在提交时一次性展示所有错误，而不是遇到第一个就中断
 */
export const collectValidationErrors = (state: IFilterState) => {
  const basicError = validateFilterBasicFields(state);
  const defaultError = validateDefaultValue(state);
  const scopeError = validateScope(state);

  return {
    // dataset / column 只需要标记错误状态，具体文案由表单项内部根据 'error' 统一处理
    datasetError: basicError?.field === 'dataset' ? 'error' : undefined,
    columnError: basicError?.field === 'column' ? ('error' as const) : undefined,
    // 类型 / 名称同样使用 'error' 占位，便于 Form.Item 统一展示“不能为空”文案
    typeError: basicError?.field === 'filterType' ? 'error' : undefined,
    nameError: basicError?.field === 'filterName' ? 'error' : undefined,
    defaultTypeError:
      state.filterType === 'date' && defaultError?.field === 'defaultType'
        ? 'error'
        : undefined,
    defaultValueError: defaultError?.field === 'defaultValue' ? 'error' : undefined,
    scopeError:
      scopeError && scopeError.field === 'scope' ? scopeError.chartErrors : undefined,
  } satisfies Partial<IValidation>;
};

const NUMERIC_COLUMN_TYPES = ['number', 'float', 'percent'];

const isScopeColumnTypeCompatible = (
  currentColumnType?: string,
  joinColumnType?: string
): boolean => {
  if (!currentColumnType || !joinColumnType) {
    return false;
  }

  if (currentColumnType === 'string' || currentColumnType === 'date') {
    return currentColumnType === joinColumnType;
  }

  if (NUMERIC_COLUMN_TYPES.includes(currentColumnType)) {
    return NUMERIC_COLUMN_TYPES.includes(joinColumnType);
  }

  return false;
};

/**
 * 校验 Scope 配置
 * @param state - 筛选器状态
 * @returns 校验错误信息，如果通过则返回 null
 */
export const validateScope = (
  state: IFilterState
): {
  field: string;
  message: string;
  chartErrors?: Record<string, string>;
} | null => {
  // 如果 scopeType 为 'specific_chart'，需要至少选择一个图表
  if (state.scope.scopeType === 'specific_chart') {
    if (!state.scope.scopeCharts || state.scope.scopeCharts.length === 0) {
      return {
        field: 'scope',
        message: 'dbl_create_dashboard_scope_join_empty',
        chartErrors: {},
      };
    }

    // 兼容旧版 Filter.tsx：
    // - 没有 applyToColumn 的图表（同源图表）跳过校验
    // - 只有显式进入关联字段链路的图表才要求非空
    const chartErrors: Record<string, string> = {};
    const currentColumnType = state.columnInfo?.columnType;
    for (const chart of state.scope.scopeCharts) {
      if (!chart.applyToColumn) {
        continue;
      }
      if (chart.applyToColumn.length === 0) {
        chartErrors[chart.taskId] = 'empty';
        continue;
      }

      const firstJoinField = chart.applyToColumn[0] as
        | { column_type?: string; columnType?: string; }
        | undefined;
      const joinColumnType =
        firstJoinField?.column_type || firstJoinField?.columnType;
      if (!isScopeColumnTypeCompatible(currentColumnType, joinColumnType)) {
        chartErrors[chart.taskId] = 'error';
      }
    }

    if (Object.keys(chartErrors).length > 0) {
      return {
        field: 'scope',
        message: 'dbl_create_dashboard_scope_join_error',
        chartErrors,
      };
    }
  }

  return null;
};

/**
 * 转换默认值配置为外部格式
 * @param filterType - 筛选器类型
 * @param defaultValue - 默认值配置
 * @returns 转换后的默认值数组
 */
export const transformDefaultValue = (
  filterType: 'date' | 'string' | 'number',
  defaultValue: IDateDefaultValue | IStringDefaultValue | INumberDefaultValue | undefined
): any[] => {
  if (!defaultValue) {
    return [];
  }

  if (filterType === 'date') {
    const dateValue = defaultValue as IDateDefaultValue;
    if (dateValue.mode === 'dynamic' && dateValue.dynamicValue) {
      return [dateValue.dynamicValue.code || `7-day`];
    }
    if (dateValue.mode === 'static' && dateValue.staticValue) {
      // 静态时间必须是 [start, end] 字符串数组
      return [dateValue.staticValue.start, dateValue.staticValue.end];
    }
  }

  if (filterType === 'string') {
    const stringValue = defaultValue as IStringDefaultValue;
    // 字符串筛选器：全选语义由 isFull / newIsFull 表达，默认值本身仍使用空数组或具体选项
    // 兼容老版行为：All 时 defaultValue 为空数组，非 All 时为具体选项集合
    return stringValue.isFull ? [] : (stringValue.value || []);
  }

  if (filterType === 'number') {
    const numberValue = defaultValue as INumberDefaultValue;
    return numberValue.value || [];
  }

  return [];
};

/**
 * 转换默认值操作符为外部格式
 * @param filterType - 筛选器类型
 * @param defaultValue - 默认值配置
 * @returns 操作符字符串
 */
export const transformDefaultOperation = (
  filterType: 'date' | 'string' | 'number',
  defaultValue: IDateDefaultValue | IStringDefaultValue | INumberDefaultValue | undefined
): string => {
  if (!defaultValue) {
    return filterType === 'string' ? 'include' : 'gt';
  }

  if (filterType === 'date') {
    const dateValue = defaultValue as IDateDefaultValue;
    if (dateValue.mode === 'dynamic') return 'recent';
    if (dateValue.mode === 'static') return 'between';
    return 'recent';
  }

  if (filterType === 'string') {
    const stringValue = defaultValue as IStringDefaultValue;
    return stringValue.operation || 'include';
  }

  if (filterType === 'number') {
    const numberValue = defaultValue as INumberDefaultValue;
    return numberValue.operation || 'gt';
  }

  return 'include';
};

/**
 * 构建筛选器配置对象
 * @param state - 筛选器状态
 * @param initialFilterId - 初始筛选器ID（编辑模式）
 * @param listChartId - 使用该数据表的全部图表 task_id（逗号拼接），写入 otherInfo.chartId 供下次编辑拉全量字段列表
 * @returns 筛选器配置对象
 */
export const buildFilterConfig = (
  state: IFilterState,
  initialFilterId?: string,
  listChartId?: string
): IFilterConfig => {
  const resolvedFilterType = resolveFilterType(state) as 'date' | 'string' | 'number';
  const filterConfig: IFilterConfig = {
    type: 'filter',
    i: initialFilterId || `filter-${Date.now()}`,
    dataset: state.dataset!,
    column: state.column!,
    filterType: resolvedFilterType,
    filterName: state.filterName,
    otherInfo: {
      columnDatasetId: state.columnInfo?.columnDatasetId || '',
      columnType: state.columnInfo?.columnType || '',
      // chartId / customColumnChartId 仅自定义字段写入：
      // - chartId: 列表请求用的多图逗号 ID（优先 listChartId，回退 columnInfo.chartId 单图 ID）
      // - customColumnChartId: 自定义字段所属的单图 ID
      // 原始表字段不需要这两个字段，编辑时由 viewList 实时计算 listChartId，回显不依赖保存值
      ...(state.columnInfo?.isCustomField ? {
        chartId: listChartId ||
          (state.columnInfo?.chartId ? String(state.columnInfo.chartId) : undefined),
        customColumnChartId: state.columnInfo?.chartId
          ? String(state.columnInfo.chartId)
          : undefined,
      } : {}),
      sqlExpression: state.columnInfo?.sqlExpression,
      isNumberColumn: state.columnInfo?.isNumeric === true,
      defaultValue: [],
    },
    defaultValue: transformDefaultValue(
      resolvedFilterType,
      state.defaultValue
    ),
    defaultOperation: transformDefaultOperation(
      resolvedFilterType,
      state.defaultValue
    ),
    /**
     * 兼容旧版 Filter.tsx 中保存的 value 字段
     * - 旧数据结构同时在筛选器配置对象上挂载 value 和 defaultValue
     * - 新结构中真实运行时值由 GeneralFilterGroup 内部的 innerFilterValueMapRef 管理
     * - 这里复用 transformDefaultValue 的结果写入 value，方便旧逻辑（如顶部筛选器删除/Scope 展示）继续工作
     * - 等相关旧逻辑全部切换为使用 innerFilterValueMapRef 或 defaultValue 后，可删除该字段
     */
    value: transformDefaultValue(
      resolvedFilterType,
      state.defaultValue
    ),
    scope: {
      scope_type: (state.scope.scopeType || 'same_dataset') as
        | 'same_dataset'
        | 'specific_chart',
      scope_charts: state.scope.scopeCharts.map(chart => ({
        task_id: chart.taskId,
        columnDatasetId: chart.columnDatasetId,
        name: chart.name,
        nameCn: chart.nameCn,
        ...(chart.applyToColumn && {
          apply_to_column: chart.applyToColumn,
        }),
        ...(chart.isDefaultDate !== undefined && {
          isDefaultDate: chart.isDefaultDate,
        }),
      })),
    },
    showScope: state.ui.showScope,
  };

  // 日期类型补充字段（用于左侧顶部筛选器正确回显）
  if (resolvedFilterType === 'date' && state.defaultValue) {
    const dateDefault = state.defaultValue as IDateDefaultValue;
    const dateType = dateDefault.defaultType || 'day';
    const from = dateDefault.from || '1';

    filterConfig.defaultType = dateType;
    filterConfig.timeType = dateType;
    filterConfig.quickValue = dateDefault.quickValue;

    // 旧版保存结构：动态日期需要 lastCount / last_day；静态日期无需
    if (dateDefault.mode === 'dynamic') {
      filterConfig.lastCount = dateDefault.dynamicValue?.count || 7;
      filterConfig.last_day = from;
    }

    // 兼容旧版字段 operation（旧逻辑大量依赖）
    filterConfig.operation = filterConfig.defaultOperation;
  }

  /**
   * 级联子筛选器配置
   *
   * 重要：上层保存逻辑使用 merge（`{ ...old, ...payload }`）更新 filter，
   * 若这里在“空数组”时省略字段，会导致旧的 relevantFilters 残留无法被清空。
   */
  /**
   * 说明：
   * - 旧实现仅在字符串类型时写入 relevantFilters，其他类型一律置空；
   *   这会导致当日期类型也支持级联子筛选器时，保存后配置被清空，
   *   再次编辑时看不到上次保存的子筛选器。
   * - 这里改为“无论筛选器类型如何，都按实际 state 写回 relevantFilters”：
   *   - 若当前没有级联配置，则写入空数组，显式覆盖旧值；
   *   - 若存在级联配置，则如实保留，支持日期/字符串等多种类型复用该能力。
   */
  filterConfig.relevantFilters = state.relevantFilters;

  // 日期类型对比时间配置（与旧版 Filter.tsx 对齐）
  if (resolvedFilterType === 'date' && state.defaultValue) {
    const dateDefault = state.defaultValue as IDateDefaultValue;

    if (Array.isArray(dateDefault.compareTimes)) {
      // 无论是否为空数组，都写回 defaultCompareTimes
      // - [] 表示明确关闭 Time Compare，覆盖旧值
      // - [..] 表示开启并保留所有对比项
      filterConfig.defaultCompareTimes = dateDefault.compareTimes;
    } else if (dateDefault.compareTime?.enabled) {
      // 兜底：只有 summary 时，至少保留一条（老数据兼容）
      filterConfig.defaultCompareTimes = [
        {
          type: dateDefault.compareTime.compareType,
          offset: dateDefault.compareTime.offset,
        },
      ];
    } else {
      // 明确写空数组，避免旧数据中的 defaultCompareTimes 残留
      filterConfig.defaultCompareTimes = [];
    }
  }

  // 字符串类型字段时间范围配置
  if (resolvedFilterType === 'string' && state.defaultValue) {
    const stringDefault = state.defaultValue as IStringDefaultValue;
    // 与旧版行为保持一致：All 语义由 isFull/newIsFull 控制，defaultValue 仍为空数组
    filterConfig.isFull = !!stringDefault.isFull;
    filterConfig.newIsFull = !!stringDefault.isFull;
    if (stringDefault.customValues) {
      // 自定义字符串值（如手动输入），用于后续请求与展示
      filterConfig.defaultCustomValues = stringDefault.customValues;
    }

    if (stringDefault.fieldTimeRange) {
      filterConfig.fieldTimeRange = {
        field: stringDefault.fieldTimeRange.field,
        fieldType: stringDefault.fieldTimeRange.fieldType,
        timeRange: {
          type: stringDefault.fieldTimeRange.timeRange.type,
          recentDays: stringDefault.fieldTimeRange.timeRange.recentDays,
        },
      };
    }
    // 上传时携带与旧版一致的 defaultFieldFilters，供后端使用（须包含 column、column_type、table_id 等完整结构）
    if (Array.isArray(stringDefault.filters) && stringDefault.filters.length > 0) {
      filterConfig.defaultFieldFilters = stringDefault.filters;
    }
  }

  return filterConfig;
};

/**
 * 聚合使用该数据表的所有图表 task_id，供 getTableInfoList 拉取带图名后缀的自定义字段列表。
 * 与 FilterDataset 中 option.chartId 的拼接规则一致。
 *
 * 修复背景：选中某自定义字段后，若仅用该字段的单图 id 请求列表，其它图上同名字段会消失。
 * 因此列表请求始终传「关联该 dataSet 的全部 chartId」，与 Select 回显用的单图 id 分开维护。
 */
export const getDatasetListChartId = (
  viewList: IViewListItem[] | undefined,
  datasetId: string | null | undefined,
): string => {
  if (!datasetId || !viewList?.length) {
    return '';
  }
  const chartIds: string[] = [];
  viewList.forEach(view => {
    const chartType = view.chartType ?? view.chart_type;
    if (chartType === 'text' || chartType === AI_ANALYSIS_TYPE) {
      return;
    }
    const taskId = view.task_id;
    if (!taskId) {
      return;
    }
    view.chart_dataset_list?.forEach(table => {
      const tableId = table?.table_id ?? table?.tableId ?? table?.id;
      if (
        tableId === datasetId &&
        !chartIds.includes(String(taskId))
      ) {
        chartIds.push(String(taskId));
      }
    });
  });
  return chartIds.join(',');
};

